import React, { Fragment, useEffect } from "react";

import Header from "./Header";
import LangSwitcher from "./LangSwitcher";
import UploadStep from "./UploadStep";
import Button from "./Button";
import FAQ from "./Faq";

import { upload as str } from "$strings";

const EXPERIENCES = {
  WEBCAM_API: "WEBCAM_API",
  FILE_PICKER: "FILE_PICKER"
};

const scrollToFAQ = () => {
  const el = document.getElementById("FAQ");
  if (el) {
    el.scrollIntoView();
  }
};

const useFAQLink = () =>
  useEffect(() => {
    const links = [].map.call(document.querySelectorAll(".Faq-link"), el => {
      el.addEventListener("click", scrollToFAQ);
      return el;
    });
    return () => {
      links.map(el => el.removeEventListener("click", scrollToFAQ));
    };
  }, []);

const IS_IOS = !!navigator.platform.match(/iPad|iPhone/gi);

const supportedExperience = () => {
  const supportsMediaRecorder = typeof window.MediaRecorder !== "undefined";
  if (supportsMediaRecorder) {
    return EXPERIENCES.WEBCAM_API;
  }
  return EXPERIENCES.FILE_PICKER;
};

const videxp = supportedExperience();

const postVideo = async blob => {
  const formData = new FormData();
  formData.append("video", blob);
  try {
    const response = await fetch(
      "https://one-in-three.appspot.com/upload-video",
      {
        method: "POST",
        body: formData
      }
    );
    const result = await response.json();
    return result;
  } catch (err) {
    console.error("Error", err);
  }
};

const scrollToTop = () => {
  window.scrollTo(0, 0);
};

const Spinner = () => (
  <div className="lds-ring">
    <div></div>
    <div></div>
    <div></div>
    <div></div>
  </div>
);

const FileUploadButton = ({ onFileSelect, invalidVideoFormat, children }) => {
  return (
    <div className="Video-file-picker">
      <label htmlFor="videoFile">{children}</label>
      <input
        onChange={onFileSelect}
        id="videoFile"
        type="file"
        accept="video/mp4,video/x-m4v,video/*"
        capture=""
      />
      {invalidVideoFormat && (
        <div className="hint error">
          {str.upload_details_a}
          <br />
          <br />
          {str.upload_details_b}
        </div>
      )}
    </div>
  );
};

const ProgressBar = ({ percent }) => {
  return (
    <div
      className="ProgressBar"
      style={{ width: `${parseInt(percent)}%` }}
    ></div>
  );
};

const StepOne = ({
  onButtonClick = () => {},
  onFileSelect = () => {},
  invalidVideoFormat
}) => {
  useFAQLink();
  return (
    <div className="Video-upload">
      {videxp === EXPERIENCES.WEBCAM_API && (
        <Fragment>
          <UploadStep completed={0}>
            <span
              dangerouslySetInnerHTML={{
                __html: str.record_video_blurb
              }}
            />
          </UploadStep>
          <Button secondary onClick={onButtonClick}>
            {str.create_video_button}
          </Button>
        </Fragment>
      )}
      {videxp === EXPERIENCES.FILE_PICKER && (
        <Fragment>
          <UploadStep completed={0}>
            {IS_IOS && (
              <p dangerouslySetInnerHTML={{ __html: str.record_video_blurb }} />
            )}
            {!IS_IOS && (
              <p dangerouslySetInnerHTML={{ __html: str.upload_video_blurb }} />
            )}
          </UploadStep>
          <FileUploadButton
            onFileSelect={onFileSelect}
            invalidVideoFormat={invalidVideoFormat}
          >
            {IS_IOS ? str.create_video_button : str.upload_video_button}
          </FileUploadButton>
        </Fragment>
      )}
      <FAQ />
    </div>
  );
};

const StepTwo = ({ children, handleRecordClick, recording }) => (
  <div className="Video-upload">
    <UploadStep completed={1}>{str.step_two_blurb}</UploadStep>
    <div className={"VideoContainer" + (!recording ? " inactive" : "")}>
      <ProgressBar percent={recording ? 100 : 0} />
      {children}
    </div>
    {!recording && (
      <div className="Record-button">
        <button onClick={handleRecordClick}>{str.record_button}</button>
      </div>
    )}
  </div>
);

const StepThree = ({
  children,
  handleCancel,
  handleRetry,
  handleSubmit,
  handleFileInput,
  invalidVideoFormat,
  previewActive,
  submitInProgress
}) => (
  <div className="Video-upload">
    <UploadStep completed={2}>{str.step_three_blurb}</UploadStep>
    <div style={{ position: "relative" }}>
      <div className="VideoContainer">{children}</div>
      {!previewActive && (
        <div className="SubmitOverlay">
          <p>{str.happy_with_video}</p>
          <Button secondary onClick={handleSubmit}>
            {submitInProgress ? <Spinner /> : str.submit_button}
          </Button>
        </div>
      )}
    </div>
    <div className="ButtonSet">
      <Button textOnly onClick={handleCancel}>
        {str.cancel_button}
      </Button>
      {videxp === EXPERIENCES.WEBCAM_API && (
        <Button secondary onClick={handleRetry}>
          {str.retry_button}
        </Button>
      )}
      {videxp === EXPERIENCES.FILE_PICKER && (
        <FileUploadButton
          onFileSelect={handleFileInput}
          invalidVideoFormat={invalidVideoFormat}
        >
          {str.retry_button}
        </FileUploadButton>
      )}
      <Button primary onClick={handleSubmit}>
        {submitInProgress ? <Spinner /> : str.submit_button}
      </Button>
    </div>
  </div>
);

const StepFour = () => {
  useFAQLink();
  return (
    <div className="Video-upload">
      <h1>{str.thank_you}</h1>
      <p>{str.step_four_blurb}</p>
      <p dangerouslySetInnerHTML={{ __html: str.still_have_questions }} />
      <FAQ />
    </div>
  );
};

export default class VideoUpload extends React.Component {
  state = {
    cameraOn: false,
    recording: false,
    recordingComplete: false,
    submitted: false,
    uploadError: null,
    previewActive: true,
    videoFile: null,
    videoFileURL: null,
    submitInProgress: false,
    invalidVideoFormat: false
  };

  constructor(props) {
    super(props);
    this.videoRef = React.createRef();
    this.previewRef = React.createRef();
    this.mediaRecorder = null;
    this.videoBlob = null;
    this.stream = "";
  }

  componentWillUnmount() {
    if (this.state.videoFileURL) {
      // this prevents memory leaks — The More You Know 🌈⭐️
      URL.revokeObjectURL(this.state.videoFileURL);
    }
  }

  handleCameraClick = () => {
    const mediaOptions = {
      audio: false,
      video: {
        width: { min: 1280 },
        height: { min: 720 }
      }
    };

    navigator.mediaDevices
      .getUserMedia(mediaOptions)
      .then(stream => {
        this.handleVideo(stream);
      })
      .catch(err => this.videoError(err));
  };

  handleVideo = stream => {
    this.stream = stream;
    this.setState({
      cameraOn: true
    });
    scrollToTop();
    this.updateVideoStream(stream);
  };

  handleFileInput = e => {
    if (
      e.target.files[0].type === "video/mp4" ||
      e.target.files[0].type === "video/quicktime"
    ) {
      this.setState({
        videoFile: e.target.files[0],
        recordingComplete: true,
        videoFileURL: URL.createObjectURL(e.target.files[0]),
        invalidVideoFormat: false
      });
    } else {
      this.setState({
        invalidVideoFormat: true
      });
    }
    scrollToTop();
  };

  handleRecordClick = () => {
    this.setState({ recording: true });
    this.mediaRecorder = new MediaRecorder(this.stream);

    this.mediaRecorder.start();
    window.setTimeout(() => {
      this.handleStopClick();
    }, 10000);
  };

  handleStopClick = e => {
    this.setState({ recording: false });
    this.mediaRecorder.ondataavailable = e => {
      this.stopCameraStream(() => {
        this.processRecording(e.data);
      });
    };
    this.mediaRecorder.stop();
  };

  stopCameraStream = done => {
    let stream = this.stream;
    let tracks = stream.getTracks();

    tracks.forEach(function(track) {
      track.stop();
    });

    this.setState({ cameraOn: false, recordingComplete: true }, done);
    scrollToTop();
  };

  processRecording = chunk => {
    this.videoBlob = new Blob([chunk], { type: "video/mp4" });
    let videoURL = window.URL.createObjectURL(this.videoBlob);
    this.updatePreviewStream(videoURL);
  };

  videoError = err => {
    alert(err.name);
  };

  updateVideoStream = stream => {
    if (this.videoRef.current.srcObject !== stream) {
      this.videoRef.current.srcObject = stream;
    }
  };

  updatePreviewStream = streamURL => {
    if (this.previewRef.current.src !== streamURL) {
      this.previewRef.current.srcObject = null;
      this.previewRef.current.src = streamURL;
    }
  };

  handleCancel = () => {
    this.setState({
      cameraOn: false,
      recording: false,
      recordingComplete: false,
      submitted: false,
      uploadError: null
    });
    scrollToTop();
  };

  handleRetry = () => {
    this.handleCancel();
    videxp === EXPERIENCES.WEBCAM_API && this.handleCameraClick();
  };

  handleSubmit = async () => {
    this.setState({
      submitInProgress: true
    });
    try {
      const result = await postVideo(
        this.videoBlob ? this.videoBlob : this.state.videoFile
      );
      if (result.success) {
        this.setState({ submitted: true, submitInProgress: false });
      }
    } catch (err) {
      this.setState({ uploadError: err, submitInProgress: false });
    }
    scrollToTop();
  };

  handlePreviewPause = () => {
    this.setState({ previewActive: false });
  };

  handlePreviewPlay = () => {
    this.setState({ previewActive: true });
  };

  render() {
    return (
      <div className="Page">
        <Header />
        <div>
          {!this.state.cameraOn && !this.state.recordingComplete && (
            <StepOne
              onButtonClick={this.handleCameraClick}
              onFileSelect={this.handleFileInput}
              invalidVideoFormat={this.state.invalidVideoFormat}
            />
          )}
          {this.state.cameraOn && (
            <StepTwo
              handleRecordClick={this.handleRecordClick}
              recording={this.state.recording}
            >
              <video
                ref={this.videoRef}
                className="Video-canvas"
                autoPlay
                controls={false}
              ></video>
            </StepTwo>
          )}
          {this.state.recordingComplete && !this.state.submitted && (
            <StepThree
              handleCancel={this.handleCancel}
              handleRetry={this.handleRetry}
              handleSubmit={this.handleSubmit}
              handleFileInput={this.handleFileInput}
              invalidVideoFormat={this.invalidVideoFormat}
              previewActive={this.state.previewActive}
              submitInProgress={this.state.submitInProgress}
            >
              {videxp === EXPERIENCES.WEBCAM_API && (
                <video
                  ref={this.previewRef}
                  autoPlay={true}
                  onPause={this.handlePreviewPause}
                  onPlay={this.handlePreviewPlay}
                  controls
                  muted
                  className="Video-canvas"
                ></video>
              )}
              {videxp === EXPERIENCES.FILE_PICKER && (
                <video
                  autoPlay={true}
                  onPause={this.handlePreviewPause}
                  onPlay={this.handlePreviewPlay}
                  controls
                  className="Video-canvas"
                  src={this.state.videoFileURL}
                ></video>
              )}
            </StepThree>
          )}
          {this.state.submitted && <StepFour></StepFour>}
        </div>
        <div className="lang-switcher">
          <LangSwitcher />
        </div>
      </div>
    );
  }
}
